/**
 * The merchants' Smart Campaigns (W10-DELIVERY-FOOD-LEVERS, D494, ADR-0111; lever L7) inside Admin › Finanças › Ofertas de incentivo: the governed co-investment bounds and, per
 * campaign, what the merchant declared, what the system picked, how much of the platform's reserved co-investment was drawn and who paid what - with the kill switch (one operator
 * ends a campaign; orders already promised keep their discount). The merchant creates and pauses its own campaigns in its console; nothing is created here. Every code shown goes
 * through the vocabulary; money is formatted in the campaign's own currency.
 */
import { type ReactElement, useState } from 'react';
import type { AdminSmartCampaignsView, SmartCampaignEndReason, SmartCampaignView } from '@vello/api-client';
import { isIso4217Currency } from '@vello/core';
import { formatMoney } from '@vello/format';
import { Badge, Button, EmptyState, Meter, Notice, Panel, Row, Select, Stack, Table, Text } from '@vello/web-theme';
import { DEFAULT_CAMPAIGN_END_REASON, bpToPercentText, campaignCanBeEnded, campaignDrawnBp, campaignStatusTone, operatorEndReasons, policyStateTone, sortCampaigns } from '../../src/index';
import { useT } from '../i18n/provider';
import { IdRef } from '../components/code-ref';
import { useVocab } from '../i18n/vocab';

export function SmartCampaignsPanel(props: {
  readonly view: AdminSmartCampaignsView | undefined;
  readonly mayManage: boolean;
  readonly busy: boolean;
  /** The market's time zone label formatter for an instant. */
  readonly when: (iso: string | null | undefined) => string;
  readonly onEnd: (campaign: SmartCampaignView, reasonCode: SmartCampaignEndReason) => Promise<void>;
}): ReactElement | null {
  const { t, lang } = useT();
  const { v } = useVocab();
  const [why, setWhy] = useState<Record<string, SmartCampaignEndReason>>({});
  const view = props.view;
  if (view === undefined) return null;
  const policy = view.policy;
  const open = policy.state === 'ACTIVE' && policy.fundState === 'ACTIVE';
  const money = (m: { readonly amountMinor: string; readonly currency: string } | undefined): string =>
    m === undefined || !/^\d+$/.test(m.amountMinor) || !isIso4217Currency(m.currency) ? '—' : formatMoney({ amountMinor: BigInt(m.amountMinor), currency: m.currency }, { locale: lang, timeZone: 'UTC', measurement: 'metric' });
  const reasons = operatorEndReasons(view);
  const terms = (c: SmartCampaignView): string => {
    if (c.mechanic === 'PERCENT_OFF') return t('smartCampaign.terms.percent', { percent: bpToPercentText(c.params.percentBp ?? 0), cap: money(c.params.perOrderCap), basket: money(c.params.minBasket) });
    if (c.mechanic === 'FIXED_OFF') return t('smartCampaign.terms.fixed', { amount: money(c.params.fixed), basket: money(c.params.minBasket) });
    return t('smartCampaign.terms.free', { cap: money(c.params.perOrderCap), basket: money(c.params.minBasket) });
  };

  return (
    <Panel padded={false}>
      <Stack gap={3}>
        <h3>{t('smartCampaign.title')}</h3>
        <Text role="caption" tone="tertiary">{t('smartCampaign.subtitle')}</Text>
        <Row gap={2} align="center" wrap>
          <Text role="caption" tone="tertiary">{t('smartCampaign.policy')}</Text>
          <Badge tone={policyStateTone(policy.state)}>{v('incentive_fund.policy_state', policy.state)}</Badge>
          <Text role="caption" tone="tertiary">{t('offers.policy.version', { version: policy.policyVersion })}</Text>
        </Row>
        {!open ? (
          <Notice tone="warning">{t('smartCampaign.needs')}</Notice>
        ) : (
          <Text role="caption" tone="tertiary">
            <span className="tnum">
              {t('smartCampaign.bounds', {
                share: bpToPercentText(policy.maxPlatformShareBp ?? 0), budget: money(policy.maxDailyBudget), coinvest: money(policy.maxCoinvestPerMerchantPerDay), days: policy.maxWindowDays ?? 0, perOrder: money(policy.maxPerOrderDiscount),
                mechanics: (policy.mechanics ?? []).map((x) => v('smart_campaign.mechanic', x)).join(', '),
              })}
            </span>
          </Text>
        )}
        <Table
          columns={[
            {
              key: 'campaign', header: t('smartCampaign.col.campaign'),
              render: (c: SmartCampaignView) => (
                <Stack gap={1}>
                  <span>{v('smart_campaign.mechanic', c.mechanic)}</span>
                  <Text role="caption" tone="tertiary"><span className="tnum">{terms(c)}</span></Text>
                  <Text role="caption" tone="tertiary">{v('smart_campaign.mechanic_reason', c.mechanicReasonCode)}</Text>
                </Stack>
              ),
            },
            { key: 'merchant', header: t('smartCampaign.col.merchant'), render: (c: SmartCampaignView) => <IdRef value={c.merchantId} /> },
            { key: 'window', header: t('smartCampaign.col.window'), render: (c: SmartCampaignView) => <span className="tnum">{t('offers.window.line', { from: props.when(c.window.from), to: props.when(c.endedAt ?? c.window.to) })}</span> },
            {
              key: 'cofund', header: t('smartCampaign.col.cofund'), numeric: true,
              render: (c: SmartCampaignView) => (
                <Stack gap={1}>
                  <Meter label={t('smartCampaign.col.cofund')} value={campaignDrawnBp(c)} max={10_000} showMax={false} warnAt={0.8} dangerAt={1} formatValue={(x) => `${Math.floor(x / 100)}%`} />
                  <Text role="caption" tone="tertiary"><span className="tnum">{t('smartCampaign.line.cofund', { drawn: money(c.usage.platformDrawn), cap: money(c.cap), left: money(c.reservation?.remaining) })}</span></Text>
                </Stack>
              ),
            },
            {
              key: 'spend', header: t('smartCampaign.col.spend'), numeric: true,
              render: (c: SmartCampaignView) => (
                <Stack gap={1}>
                  <span className="tnum">{t('smartCampaign.line.orders', { orders: c.usage.orders, discount: money(c.usage.discount) })}</span>
                  <Text role="caption" tone="tertiary"><span className="tnum">{t('smartCampaign.line.split', { platform: money(c.usage.platformPromised), merchant: money(c.usage.merchantFunded) })}</span></Text>
                  <Text role="caption" tone="tertiary"><span className="tnum">{t('smartCampaign.line.budget', { budget: money(c.dailyBudget), share: bpToPercentText(c.platformShareBp) })}</span></Text>
                </Stack>
              ),
            },
            { key: 'state', header: t('smartCampaign.col.state'), render: (c: SmartCampaignView) => <Badge tone={campaignStatusTone(c.status)}>{v('smart_campaign.status', c.status)}</Badge> },
            {
              key: 'action', header: t('smartCampaign.col.action'),
              render: (c: SmartCampaignView) => {
                if (!campaignCanBeEnded(c)) return <span>{c.endReason !== null ? v('smart_campaign.end_reason', c.endReason) : '—'}</span>;
                if (!props.mayManage) return <span>—</span>;
                const chosen = why[c.campaignId] ?? DEFAULT_CAMPAIGN_END_REASON;
                return (
                  <Stack gap={2}>
                    <Select aria-label={t('smartCampaign.end.reason')} value={chosen} onChange={(ev) => setWhy((r) => ({ ...r, [c.campaignId]: ev.target.value as SmartCampaignEndReason }))}>
                      {reasons.map((r) => (
                        <option key={r} value={r}>{v('smart_campaign.end_reason', r)}</option>
                      ))}
                    </Select>
                    <Button type="button" variant="danger" disabled={props.busy} onClick={() => void props.onEnd(c, chosen)}>{t('smartCampaign.end.button')}</Button>
                  </Stack>
                );
              },
            },
          ]}
          rows={sortCampaigns(view.campaigns)}
          rowKey={(c) => c.campaignId}
          empty={<EmptyState title={t('smartCampaign.empty')} />}
        />
      </Stack>
    </Panel>
  );
}
